import Vuex from "vuex";

import BusinessIndustryService from "../services/business-industry-service";
import VersionService from "../services/version-service";
import UserManagementService from "../services/user-management-service";
import subscriptionService from "@/services/subscription-service";

interface CollapseState {
  [key: string]: boolean;
}

export default new Vuex.Store({
  state: {
    recaptchaSiteKey: <string | null>null,
    appInitComplete: <Boolean> false,
    jwtToken: <string | null>null,
    email: <string | null>"",
    userID: <number | null>null,
    rememberMe: <boolean | null>false,
    userGroup: "User",
    industries: [],
    isOwner: <boolean | null>false,
    xeroClientID: <string | null>"",
    xeroRedirectURI: <string | null>"",
    xeroReferralRedirectURI: <string | null>"",
    xeroTenantRedirectURI: <string | null>"",
    stripePublishableKey: <string | null>"",
    stripeSuccessUrl: <string | null>"",
    isTrial: <boolean | null>false,
    isSubscribed: <boolean | null>false,
    loggedInAsUserID: <number | null>null,
    subscriptionCancelled: <boolean | null>false,
    hasPaymentMethod: <boolean | null>false,
    subDetailsLoaded: <boolean | null>false,
    givenName: <string | null>"",
    surname: <string | null>"",
    phoneNumber: <string | null>"",
    businessName: <string | null>"",
    xeroTenantName: <string | null>"",
    businessID: <number | null> null,
    organisationType: <number | null> null,
    appVersion: <string | null>"",
    loggedInFirstName: <string | null>"",
    isLoading: <boolean | null>false,
    isSyncing: <boolean | null>false,
    isXeroUser: <boolean | null>false,
    initialSetUpCompleted: <boolean | null>false,
    twoFactorEnabled: <boolean | null>false,
    cancelled: <boolean | null>false,
    xeroConnected: <boolean | null>false,
    helpSectionEnabled: <boolean | null>true,
    billingEmail: <string | null>"",
    appDataReady: <boolean | null>true,
    plExportMode: <boolean | null>false,
    onRouterChange: <boolean | null>false,
    twoFactorGUID: <any | null>null,
    whatIfOptionID: <string | null>"",
    whatIfOptionIndex: <string | null>"",
    tempXeroToken: <Object | null> null,
    tempXeroTenant: <Object | null>null,
    collapse: {
      "businessSummary": false,
      "atoPayments": false,
      "financePayments": false,
      "businessPayments": false,
      "personalPayments": false,
      "cashBalance": false,
      "expense": false,
      "income": false,
      "sales": true
    } as CollapseState,
    tutorialWizardUpdate: <boolean | null>false,
    cashSummaryWizardGuideOpen: <boolean | null>false,
    selectedPeriod: <string>"Current",
  },
  getters: {
    isSectionCollapsed: (state) => (section: string) => {
      return state.collapse[section];
    },
    appInitComplete(state) {
      return state.appInitComplete;
    },
    isLoggedIn(state) {
      return state.loggedInAsUserID != null;
    },
    isOwner(state) {
      return state.isOwner;
    },
    appDataReady(state) {
      return state.appDataReady;
    },
    isAdmin(state) {
      return state.userGroup === "Admin"
    },
    isAffiliate(state) {
      return state.userGroup === "Affiliate"
    },
    userID(state) {
      return state.userID;
    },
    email(state) {
      return state.email;
    },
    jwtToken(state) {
      return state.jwtToken;
    },
    rememberMe(state) {
      return state.rememberMe;
    },
    industries(state) {
      return state.industries;
    },
    xeroClientID(state) {
      return state.xeroClientID;
    },
    xeroRedirectURI(state) {
      return state.xeroRedirectURI;
    },
    xeroReferralRedirectURI(state) {
      return state.xeroReferralRedirectURI;
    },
    xeroTenantRedirectURI(state) {
      return state.xeroTenantRedirectURI;
    },
    loggedInAsUserID(state) {
      return state.loggedInAsUserID;
    },
    stripePublishableKey(state) {
      return state.stripePublishableKey;
    },
    stripeSuccessUrl(state) {
      return state.stripeSuccessUrl;
    },
    isSubscribed(state) {
      return state.isSubscribed;
    },
    isTrial(state) {
      return state.isTrial;
    },
    isSubscriptionLocked(state) {
      return !state.isSubscribed;
    },
    isSubscriptionCancelled(state) {
      return state.subscriptionCancelled;
    },
    hasPaymentMethod(state) {
      return state.hasPaymentMethod;
    },
    isSubDetailsLoaded(state) {
      return state.subDetailsLoaded;
    },
    givenName(state) {
      return state.givenName;
    },
    surname(state) {
      return state.surname;
    },
    phoneNumber(state) {
      return state.phoneNumber;
    },
    businessName(state) {
      return state.businessName;
    },
    xeroTenantName(state) {
      return state.xeroTenantName;
    },
    cancelled(state) {
      return state.cancelled;
    },
    businessID(state) {
      return state.businessID;
    },
    organisationType(state) {
      return state.organisationType;
    },
    appVersion(state) {
      return state.appVersion;
    },
    loggedInFirstName(state) {
      return state.loggedInFirstName;
    },
    isLoading(state) {
      return state.isLoading;
    },
    isSyncing(state) {
      return state.isSyncing;
    },
    isXeroUser(state) {
      return state.isXeroUser;
    },
    initialSetUpCompleted(state) {
      return state.initialSetUpCompleted;
    },
    twoFactorEnabled(state) {
      return state.twoFactorEnabled;
    },
    xeroConnected(state) {
      return state.xeroConnected;
    },
    helpSectionEnabled(state) {
      return state.helpSectionEnabled;
    },
    billingEmail(state) {
      return state.billingEmail;
    },
    whatIfOptionID(state) {
      return state.whatIfOptionID;
    },
    whatIfOptionIndex(state) {
      return state.whatIfOptionIndex;
    },
    plExportMode(state) {
      return state.plExportMode;
    },
    onRouterChange(state) {
      return state.onRouterChange;
    },
    twoFactorGUID(state) {
      return state.twoFactorGUID;
    },
    tempXeroToken(state) {
      return state.tempXeroToken
    },
    tempXeroTenant(state) {
      return state.tempXeroTenant
    },
    isImpersonatingUser(state) {
      return state.userGroup === "Admin" && state.businessID;
    },
    tutorialWizardUpdate(state) {
      return state.tutorialWizardUpdate
    },
    cashSummaryWizardGuideOpen(state) {
      return state.cashSummaryWizardGuideOpen;
    },
    selectedPeriod(state) {
      return state.selectedPeriod;
    },
    recaptchaSiteKey(state) {
      return state.recaptchaSiteKey;
    },
  },
  mutations: {
    setCollapsableSection(state, section: string) {
      if (section in state.collapse) {
        state.collapse[section] = !state.collapse[section];
      }
    },
    setAppInitComplete(state, payload?) {
      if (!payload === null || payload === undefined) {
        state.appInitComplete = false
      } else {
        state.appInitComplete = payload;
      }
    },
    setUserEmail(state, payload?) {
      if (payload === null || payload === undefined) {
        state.email = ""
        localStorage.removeItem("email");
      } else {
        state.email = payload;
        localStorage.setItem("email", payload);
      }
    },
    setUserID(state, payload?: number) {
      if (payload === null || payload === undefined || Number.isNaN(payload)) {
        state.userID = null
        localStorage.removeItem("userid");
      } else {
        state.userID = payload;
        localStorage.setItem("userid", payload.toString());
      }
    },
    setJwtToken(state, payload?) {
      if (payload === null) {
        state.jwtToken = null;
        localStorage.removeItem("jwt_token");
      } else {
        state.jwtToken = payload;
        localStorage.setItem("jwt_token", payload);
      }
    },
    setRememberMe(state, payload?) {
      if (payload === null) {
        state.rememberMe = null;
        localStorage.removeItem("remember_me");
      } else {
        state.rememberMe = payload;
        localStorage.setItem("remember_me", payload);
      }
    },
    setUserGroup(state, payload?) {
      if (payload === null) {
        state.userGroup = "User";
        localStorage.removeItem("user_group");
      } else {
        state.userGroup = payload;
        localStorage.setItem("user_group", payload);
      }
    },
    setIsOwner(state, payload?) {
      if (payload === null) {
        state.isOwner = false;
        localStorage.removeItem("is_owner");
      } else {
        state.isOwner = payload;
        localStorage.setItem("is_owner", payload);
      }
    },
    setAppDataReady(state, payload?) {
      if (payload === null) {
        state.appDataReady = false;
      } else {
        state.appDataReady = payload;
      }
    },
    setXeroClientID(state, payload?) {
      if (payload === null) {
        state.xeroClientID = "";
        localStorage.removeItem("xero_client_id");
      } else {
        state.xeroClientID = payload;
        localStorage.setItem("xero_client_id", payload);
      }
    },
    setXeroRedirectURI(state, payload?) {
      if (payload === null) {
        state.xeroRedirectURI = "";
        localStorage.removeItem("xero_redirect_uri");
      } else {
        state.xeroRedirectURI = payload;
        localStorage.setItem("xero_redirect_uri", payload);
      }
    },
    setXeroReferralRedirectURI(state, payload?) {
      if (payload === null) {
        state.xeroReferralRedirectURI = "";
        localStorage.removeItem("xero_referral_redirect_uri");
      } else {
        state.xeroReferralRedirectURI = payload;
        localStorage.setItem("xero_referral_redirect_uri", payload);
      }
    },
    setXeroTenantRedirectURI(state, payload?) {
      if (payload === null) {
        state.xeroTenantRedirectURI = "";
        localStorage.removeItem("xero_tenant_redirect_uri");
      } else {
        state.xeroTenantRedirectURI = payload;
        localStorage.setItem("xero_tenant_redirect_uri", payload);
      }
    },
    setBusinessIndustries(state, val?) {
      state.industries = val;
    },
    setLoggedInAsUserID(state, payload?) {
      if (payload === null) {
        state.loggedInAsUserID = null;
        localStorage.removeItem("logged_in_as_user_id");
      } else {
        state.loggedInAsUserID = payload;
        localStorage.setItem("logged_in_as_user_id", payload);
      }
    },
    setStripePublishableKey(state, payload?) {
      if (payload === null) {
        state.stripePublishableKey = "";
      } else {
        state.stripePublishableKey = payload;
      }
    },
    setStripeSuccessUrl(state, payload?) {
      if (payload === null) {
        state.stripeSuccessUrl = "";
      } else {
        state.stripeSuccessUrl = payload;
      }
    },
    setIsSubscribed(state, payload?) {
      if (payload === null) {
        state.isSubscribed = false;
      } else {
        state.isSubscribed = payload;
      }
    },
    setIsTrial(state, payload?) {
      if (payload === null) {
        state.isTrial = false;
      } else {
        state.isTrial = payload;
      }
    },
    setIsSubscriptionCancelled(state, payload?) {
      if (payload === null) {
        state.subscriptionCancelled = false;
      } else {
        state.subscriptionCancelled = payload;
      }
    },
    setHasPaymentMethod(state, payload?) {
      if (payload === null) {
        state.hasPaymentMethod = false;
      } else {
        state.hasPaymentMethod = payload;
      }
    },
    setIsSubDetailsLoaded(state, payload?) {
      if (payload === null) {
        state.subDetailsLoaded = false;
      } else {
        state.subDetailsLoaded = payload;
      }
    },
    setGivenName(state, payload?) {
      if (payload === null) {
        state.givenName = "";
      } else {
        state.givenName = payload;
      }
    },
    setSurname(state, payload?) {
      if (payload === null) {
        state.surname = "";
      } else {
        state.surname = payload;
      }
    },
    setPhoneNumber(state, payload?) {
      if (payload === null) {
        state.phoneNumber = "";
      } else {
        state.phoneNumber = payload;
      }
    },
    setBusinessName(state, payload?) {
      if (payload === null) {
        state.businessName = "";
        localStorage.removeItem("business_name");
      } else {
        localStorage.setItem("business_name", payload.toString());
        state.businessName = payload;
      }
    },
    setXeroTenantName(state, payload?) {
      if (payload === null) {
        state.xeroTenantName = "";
        localStorage.removeItem("xero_tenant_name");
      } else {
        localStorage.setItem("xero_tenant_name", payload.toString());
        state.xeroTenantName = payload;
      }
    },
    setBillingEmail(state, payload?) {
      if (payload === null) {
        state.billingEmail = "";
      } else {
        state.billingEmail = payload;
      }
    },
    setWhatIfOptionID(state, payload?) {
      if (payload === null) {
        state.whatIfOptionID = "";
      } else {
        state.whatIfOptionID = payload;
      }
    },
    setWhatIfOptionIndex(state, payload?) {
      if (payload === null) {
        state.whatIfOptionIndex = "";
      } else {
        state.whatIfOptionIndex = payload;
      }
    },
    setCancelled(state, payload?) {
      if (payload === null) {
        state.cancelled = false;
      } else {
        state.cancelled = payload;
      }
    },
    setBusinessID(state, payload?: number) {
      if (payload === null || payload === undefined || Number.isNaN(payload)) {
        state.businessID = null
        localStorage.removeItem("business_id");
      } else {
        state.businessID = payload;
        localStorage.setItem("business_id", payload.toString());
      }
    },
    setOrganisationType(state, payload?: number) {
      if (payload === null || payload === undefined || Number.isNaN(payload)) {
        state.organisationType = null
        localStorage.removeItem("organisation_type");
      } else {
        state.organisationType = payload;
        localStorage.setItem("organisation_type", payload.toString());
      }
    },
    setAppVersion(state, payload?) {
      if (payload === null) {
        state.appVersion = "";
      } else {
        state.appVersion = payload;
      }
    },
    setLoggedInFirstName(state, payload?) {
      if (payload === null) {
        state.loggedInFirstName = "";
      } else {
        state.loggedInFirstName = payload;
      }
    },
    setIsLoading(state, payload?) {
      if (payload === null) {
        state.isLoading = false;
      } else {
        state.isLoading = payload;
      }
    },
    setIsSyncing(state, payload?) {
      if (payload === null) {
        state.isSyncing = false;
      } else {
        state.isSyncing = payload;
      }
    },
    setIsXeroUser(state, payload?) {
      if (payload === null) {
        state.isXeroUser = false;
      } else {
        state.isXeroUser = payload;
      }
    },
    setInitialSetUpCompleted(state, payload?) {
      if (payload === null) {
        state.initialSetUpCompleted = false;
        localStorage.removeItem("initialSetUpCompleted");
      } else {
        state.initialSetUpCompleted = payload;
        localStorage.setItem("initialSetUpCompleted", payload);
      }
    },
    setTwoFactorEnabled(state, payload?) {
      if (payload === null) {
        state.twoFactorEnabled = false;
      } else {
        state.twoFactorEnabled = payload;
      }
    },
    setXeroConnected(state, payload?) {
      if (payload === null) {
        state.xeroConnected = null;
        localStorage.removeItem("xero_connected");
      } else {
        state.xeroConnected = payload;
        localStorage.setItem("xero_connected", payload);
      }
    },
    setHelpSectionEnabled(state, payload?) {
      if (payload === null) {
        state.helpSectionEnabled = false;
      } else {
        state.helpSectionEnabled = payload;
      }
    },
    setPlExportMode(state, payload?) {
      if (payload === null) {
        state.plExportMode = false;
      } else {
        state.plExportMode = payload;
      }
    },
    setOnRouterChange(state, payload?) {
      if (payload === null) {
        state.onRouterChange = false;
      } else {
        state.onRouterChange = payload;
      }
    },
    setTwoFactorGUID(state, payload?) {
      if (payload === null) {
        state.twoFactorGUID = null;
      } else {
        state.twoFactorGUID = payload;
      }
    },
    setTempXeroToken(state, payload?) {
      if (payload === null) {
        state.tempXeroToken = null;
      } else {
        state.tempXeroToken = payload;
      }
    },
    setTempXeroTenant(state, payload) {
      if (payload === null) {
        state.tempXeroTenant = null;
      } else {
        state.tempXeroTenant = payload;
      }
    },
    tutorialWizardUpdated(state) {
      state.tutorialWizardUpdate = !state.tutorialWizardUpdate;
    },
    setCashSummaryWizardGuideOpen(state, payload) {
      if (payload === null) {
        state.cashSummaryWizardGuideOpen = null;
      } else {
        state.cashSummaryWizardGuideOpen = payload;
      }
    },
    setSelectedPeriod(state, payload) {
      if (payload === null) {
        state.selectedPeriod = "Current";
        localStorage.setItem("selectedPeriod", "Current");
      } else {
        state.selectedPeriod = payload;
        localStorage.setItem("selectedPeriod", payload);
      }
    },
    setRecaptchaSiteKey(state, payload) {
      if (payload === null) {
        state.recaptchaSiteKey = null;
      } else {
        state.recaptchaSiteKey = payload;
      }
    }
  },
  actions: {
    updateCollapsableSection({ commit }, section: string) {
      commit('setCollapsableSection', section);
    },
    appInitComplete({ state, commit }, payload) {
      commit("setAppInitComplete", payload);
    },
    initialise({ state, commit }) {
      const initialToken = localStorage.getItem("jwt_token");
      commit("setJwtToken", initialToken);
      const email = localStorage.getItem("email");
      commit("setUserEmail", email);
      const userid = parseInt(localStorage.getItem("userid") ?? "");
      commit("setUserID", isNaN(userid) ? null : userid);
      const rememberMe = localStorage.getItem("remember_me") === "true"
      commit("setRememberMe", rememberMe);
      const userGroup = localStorage.getItem("user_group");
      commit("setUserGroup", userGroup);
      const isOwner = localStorage.getItem("is_owner") === "true"
      commit("setIsOwner", isOwner);
      const xeroClientID = localStorage.getItem("xero_client_id");
      commit("setXeroClientID", xeroClientID);
      const xeroRedirectURI = localStorage.getItem("xero_redirect_uri");
      commit("setXeroRedirectURI", xeroRedirectURI);
      const xeroReferralRedirectURI = localStorage.getItem("xero_referral_redirect_uri");
      commit("setXeroReferralRedirectURI", xeroReferralRedirectURI);
      const xeroTenantRedirectURI = localStorage.getItem("xero_tenant_redirect_uri");
      commit("setXeroTenantRedirectURI", xeroTenantRedirectURI);
      const loggedInAsUserID = localStorage.getItem("logged_in_as_user_id");
      commit("setLoggedInAsUserID", loggedInAsUserID);
      const xeroConnected = localStorage.getItem("xero_connected") === "true";
      commit("setXeroConnected", xeroConnected);
      const initialSetUpCompleted = localStorage.getItem("initialSetUpCompleted") === "true";
      commit("setInitialSetUpCompleted", initialSetUpCompleted);
      const businessID = parseInt(localStorage.getItem("business_id") ?? "");
      commit("setBusinessID", isNaN(businessID) ? null : businessID);
      const businessName = localStorage.getItem("business_name") ?? "";
      commit("setBusinessName", businessName);
      const xeroTenantName = localStorage.getItem("xero_tenant_name") ?? "";
      commit("setXeroTenantName", xeroTenantName);
      const organisationType = parseInt(localStorage.getItem("organisation_type") ?? "");
      commit("setOrganisationType", isNaN(organisationType) ? null : organisationType);
      const selectedPeriod = localStorage.getItem("selectedPeriod") ?? "Current"
      commit("setSelectedPeriod", selectedPeriod);
    },
    updateLogin({ state, commit }, payload) {
      commit("setJwtToken", payload.accessToken);
      commit("setUserEmail", payload.email);
      commit("setUserID", payload.userID);
      commit("setUserGroup", payload.userGroup);
      commit("setIsOwner", payload.isOwner);
      commit("setIsSubscribed", payload.subscriptionStatus.active);
      commit("setIsTrial", payload.subscriptionStatus.trial);
      commit("setIsSubscriptionCancelled", payload.subscriptionStatus.cancelled);
      commit("setHasPaymentMethod", payload.subscriptionStatus.hasPaymentMethod);
      commit("setIsSubDetailsLoaded", true);
      commit("setLoggedInFirstName", payload.firstName);
      commit("setLoggedInAsUserID", payload.userID);
      commit("setPhoneNumber", payload.phoneNumber);
      commit("setIsXeroUser", payload.isXeroUser);
      commit("setInitialSetUpCompleted", payload.initialSetUpCompleted);
      commit("setTwoFactorEnabled", payload.twoFactorEnabled);
      commit("setHelpSectionEnabled", payload.helpSectionEnabled);
      commit("setTwoFactorGUID", payload.twoFactorGUID);
      commit("setBusinessID", payload.businessID);
      commit("setBusinessName", payload.businessName);
      commit("setXeroTenantName", payload.xeroTenantName);
      commit("setOrganisationType", payload.organisationType)
      commit("setSelectedPeriod", localStorage.getItem("selectedPeriod") ?? "Current");

      sessionStorage.setItem("sessionAuthenticated", payload.authenticated);
    },
    updateTwoFactorGUID({ commit }, payload) {
      commit("setTwoFactorGUID", payload.twoFactorGUID);
    },
    updateImpersonateLogin({ commit }, payload) {
      // Other important background settings
      commit("setBusinessID", payload.businessID);
      commit("setBusinessName", payload.businessName);
      commit("setXeroTenantName", payload.xeroTenantName);
      commit("setOrganisationType", payload.organisationType)
    },
    updateXeroConfig({ commit }, payload) {
      commit("setXeroClientID", payload.clientID);
      commit("setXeroRedirectURI", payload.redirectURI);
      commit("setXeroReferralRedirectURI", payload.referralRedirectURI);
      commit("setXeroTenantRedirectURI", payload.tenantRedirectURI);
    },
    clearJWTToken({ commit }) {
      commit("setJwtToken", null);
      commit("setIsOwner", null);
      commit("setIsSubscribed", null);
      commit("setIsTrial", null);
      commit("setIsSubscriptionCancelled", null);
      commit("setHasPaymentMethod", null);
      commit("setIsSubDetailsLoaded", null);
      commit("setXeroConnected", null);
      commit("setBusinessID", null);
      commit("setXeroTenantName", null);
      commit("setOrganisationType", null);
    },
    clearLogin({ commit }) {
      sessionStorage.setItem("sessionAuthenticated", "false");

      commit("setJwtToken", null);
      commit("setUserEmail", null);
      commit("setUserEmail", null);
      commit("setIsOwner", null);
      commit("setUserID", null);
      commit("setUserGroup", null);
      commit("setIsSubscribed", null);
      commit("setIsTrial", null);
      commit("setIsSubscriptionCancelled", null);
      commit("setHasPaymentMethod", null);
      commit("setIsSubDetailsLoaded", null);
      commit("setPhoneNumber", null);
      commit("setIsXeroUser", null);
      commit("setInitialSetUpCompleted", null);
      commit("setLoggedInAsUserID", null);
      commit("setTwoFactorEnabled", null);
      commit("setXeroClientID", null);
      commit("setXeroRedirectURI", null);
      commit("setXeroReferralRedirectURI", null);
      commit("setXeroTenantRedirectURI", null);
      commit("setXeroConnected", null);
      commit("setHelpSectionEnabled", null);
      commit("setTwoFactorGUID", null);
      commit("setXeroConnected", null);
      commit("setBusinessID", null);
      commit("setXeroTenantName", null);
      commit("setOrganisationType", null);
      commit("setSelectedPeriod", "Current");
    },
    clearImpersonateLogin({ state, commit }) {
      // Other settings to clear
      commit("setXeroConnected", null);
      commit("setBusinessID", null);
      commit("setXeroTenantName", null);
      commit("setOrganisationType", null);
      commit("setIsOwner", null);
    },
    loadBusinessIndustries({ state, commit }) {
      if (state.industries.length === 0) {
        BusinessIndustryService.list().then((response) => {
          commit("setBusinessIndustries", response.data);
        });
      }
    },
    updateLoggedInAsUserID({ commit }, payload) {
      commit("setLoggedInAsUserID", payload);
    },
    updateCancelled({ commit }, payload) {
      commit("setCancelled", payload.cancelled);
    },
    updateWhatIfOptionID({ commit }, payload) {
      commit("setWhatIfOptionID", payload);
    },
    updateWhatIfOptionIndex({ commit }, payload) {
      commit("setWhatIfOptionIndex", payload);
    },
    setStripeConfig({ commit }, payload) {
      commit("setStripePublishableKey", payload.stripePublishableKey);
      commit("setStripeSuccessUrl", payload.stripeSuccessUrl);
    },
    async updateSubscriptionState({ state, commit }) {
      if (state.userID !== null && !Number.isNaN(state.userID)) {
        if (state.userGroup === "Admin") { // Admin bypasses all subscriptions checks (which we still handle server-side)
          commit("setIsSubscriptionCancelled", false);
          commit("setIsTrial", false);
          commit("setIsSubscribed", false);
        }
        else if (state.businessID !== null && !Number.isNaN(state.businessID)) {
          await subscriptionService.getSubscriptionInformation(state.businessID).then((response: any) => {
            if (response.activeSubscription) {
              commit("setIsSubscriptionCancelled", response.activeSubscription.isCancelled);
              commit("setIsTrial", response.activeSubscription.isTrial);
              commit("setIsSubscribed", true);
              commit("setIsSubDetailsLoaded", true);
            }
            else {
              commit("setIsSubscriptionCancelled", false);
              commit("setIsTrial", false);
              commit("setIsSubscribed", false);
              commit("setIsSubDetailsLoaded", true);
            }
          });
        }
        else {
          commit("setIsSubscriptionCancelled", true);
          commit("setIsTrial", false);
          commit("setIsSubscribed", false);
          commit("setIsSubDetailsLoaded", true);
        }
      }
      else {
        commit("setIsSubscriptionCancelled", true);
        commit("setHasPaymentMethod", false);
        commit("setIsTrial", false);
        commit("setIsSubscribed", false);
        commit("setIsSubDetailsLoaded", true);
      }
    },
    loadAppVersion({ state, commit }) {
      if (!state.appVersion) {
        VersionService.getVersion().then((response) => {
          if (response.data) {
            commit("setAppVersion", response.data.value);
          }
        });
      }
    },
    setIsOwnerTrue({ commit }) {
      commit("setIsOwner", true);
    },
    setIsOwner({ commit }, payload) {
      commit("setIsOwner", payload);
    },
    setAppDataReady({ commit }, payload) {
      commit("setAppDataReady", payload);
    },
    async loadLoggedInFirstName({ commit }) {
      await UserManagementService.getCurrentUserProfile().then((response) => {
        commit("setLoggedInFirstName", response.data.firstName);
      });
    },
    updateInitialSetUpCompleted({ commit }, payload) {
      commit("setInitialSetUpCompleted", payload);
    },
    updateXeroConnected({ commit }, payload) {
      commit("setXeroConnected", payload);
    },
    setTempXeroData({ commit }, payload) {
      commit("setTempXeroToken", payload.token);
      commit("setTempXeroTenant", payload.tenant);
    },
    clearTempXeroData({ commit }) {
      commit("setTempXeroToken", null);
      commit("setTempXeroTenant", null);
    },
    tutorialWizardUpdate({ commit }) {
      commit("tutorialWizardUpdated");
    },
    setCashSummaryWizardGuideOpen({ commit }, payload) {
      commit("setCashSummaryWizardGuideOpen", payload);
    },
    setSelectedPeriod({ commit }, payload) {
      commit("setSelectedPeriod", payload);
    }
  }
});
